import { Stack, Typography } from '@mui/material';
import { getLeadReport } from 'API/lead';
import { DetailResponse, ResParticipant } from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import { saveAs } from 'file-saver';
import { useUserData } from 'hooks/use-user-data';
import React, { useState } from 'react';
import useErrorStore from 'stores/error-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import ReportLanguageDialog from '../../report-language-dialog';
import ParticipantList from '../participants/participant-list';
import LeadQuestions from './lead-questions';

interface Props {
  details: DetailResponse;

  handleOpenDuplicateQuestionnaireDialog(): void;

  handleOpenAddParticipantsDialog(): void;

  copyToClipboard(id: ResParticipant): () => void;

  copyToClipboardAnonymous(): void;

  getRealPerspective(id: string): string;

  handleEditParticipantIsSuspended(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleEditParticipantRole(id: string): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const QuestionnaireDetailsView: React.FC<Props> = ({
  details,
  handleOpenDuplicateQuestionnaireDialog,
  handleOpenAddParticipantsDialog,
  copyToClipboard,
  copyToClipboardAnonymous,
  getRealPerspective,
  handleEditParticipantIsSuspended,
  handleEditParticipantRole,
}) => {
  const { classes, cx } = useStyles();
  useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [reportLanguageDialog, setReportLanguageDialog] = useState<{ open: boolean; language: string; loading: boolean }>({
    open: false,
    language: 'de',
    loading: false,
  });

  /**
   * Report language dialog handlers
   */

  const handleOpenReportLanguageDialog = () => {
    setReportLanguageDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseReportLanguageDialog = () => {
    setReportLanguageDialog({ open: false, language: 'de', loading: false });
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportLanguageDialog((prev) => ({ ...prev, language: event.target.value }));
  };

  const createPDF = async () => {
    setReportLanguageDialog((prev) => ({ ...prev, loading: true }));

    const res = await getLeadReport(details.id, reportLanguageDialog.language);
    if (!res.error) {
      const newBlob = new Blob([res], { type: 'application/pdf' });
      saveAs(newBlob, `Report-${details.id}`);

      handleCloseReportLanguageDialog();
    } else {
      setReportLanguageDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/lead/reports/:leadTagId', 'get');
    }
  };

  return (
    <>
      <Stack className={classes.topActions} direction={'row'} alignItems={'center'} gap={2}>
        <PaiLink
          text={'Fragenkatalog duplizieren'}
          variant={'primary'}
          underline={'always'}
          size={'md'}
          action={handleOpenDuplicateQuestionnaireDialog}
        />
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Typography className={classes.text}>Umfragestatus:</Typography>
          {!details.completed ? (
            <Typography className={cx(classes.text, 'warning')}>ausstehend</Typography>
          ) : (
            <PaiButton text={'Ergebnis'} size={'small'} action={handleOpenReportLanguageDialog} style={{ marginLeft: '8px' }} />
          )}
        </Stack>
      </Stack>
      <Stack className={classes.leadDetails} direction={{ xs: 'column', xl: 'row' }} gap={1}>
        <div className={cx(classes.content, 'large')}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>Fragenkatalog von {details.name}</Typography>
          </div>
          <LeadQuestions details={details} />
        </div>
        <div className={cx(classes.content, 'small')}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>Feedbackgeber</Typography>
          </div>
          <ParticipantList
            details={details}
            handleOpenAddParticipantsDialog={handleOpenAddParticipantsDialog}
            copyToClipboard={copyToClipboard}
            copyToClipboardAnonymous={copyToClipboardAnonymous}
            getRealPerspective={getRealPerspective}
            handleEditParticipantIsSuspended={handleEditParticipantIsSuspended}
            handleEditParticipantRole={handleEditParticipantRole}
          />
        </div>
      </Stack>

      <ReportLanguageDialog
        open={reportLanguageDialog.open}
        language={reportLanguageDialog.language}
        loading={reportLanguageDialog.loading}
        handleLanguageChange={handleLanguageChange}
        handleClose={handleCloseReportLanguageDialog}
        handleAcceptLanguage={createPDF}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  topActions: {
    position: 'absolute',
    right: '32px',
    top: 0,
    height: '60px',
    zIndex: 1300,
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    marginTop: '1.5px',
    '&.warning': {
      color: theme.palette.warning.main,
    },
  },
  leadDetails: {
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    maxHeight: 'calc(960px - 60px - 32px)',
    [theme.breakpoints.down('xl')]: {
      height: 'fit-content',
      maxHeight: 'unset',
    },
  },
  content: {
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '12px 16px',
    height: '100%',
    '&.small': {
      flexBasis: '33%',
    },
    '&.large': {
      flexBasis: '67%',
    },
    [theme.breakpoints.down('xl')]: {
      flexBasis: '100%',
    },
  },
  title: {
    marginBottom: '32px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
}));

export default QuestionnaireDetailsView;
